import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, ArrowLeft, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('').filter((i) => i.to !== '/shop/wicks') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

function MrsBumSuccess() {
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      fetchOrderDetails(sessionId);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchOrderDetails = async (sessionId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/mrsbum/checkout/status/${sessionId}`);
      setOrderDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch order details:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} />

      {/* Main Content */}
      <main style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '60px 20px'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center' }}>
            <div className="loading-spinner" />
          </div>
        ) : (
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '40px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            textAlign: 'center'
          }}>
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 25px'
            }}>
              <CheckCircle size={40} color="#7c3aed" />
            </div>

            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '28px',
              color: '#1a1a1a',
              marginBottom: '10px'
            }}>
              Thank You!
            </h1>
            
            <p style={{
              color: '#666',
              fontSize: '16px',
              marginBottom: '30px'
            }}>
              Your Mrs. Bum order has been placed successfully.
            </p>

            {orderDetails?.order && (
              <div style={{
                background: '#f3e8ff',
                borderRadius: '12px',
                padding: '25px',
                marginBottom: '30px',
                textAlign: 'left'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '15px',
                  color: '#6b21a8'
                }}>
                  <Package size={20} />
                  <span style={{ fontWeight: '600' }}>Order Details</span>
                </div>

                <div style={{ 
                  display: 'grid', 
                  gap: '10px',
                  fontSize: '14px' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Order Number:</span>
                    <span style={{ fontWeight: '600', color: '#1a1a1a' }}>
                      {orderDetails.order.order_number}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#666' }}>Item:</span>
                    <span style={{ fontWeight: '600', color: '#1a1a1a' }}>
                      {orderDetails.order.product_title}
                    </span>
                  </div>

                  {orderDetails.order.size && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Size:</span>
                      <span style={{ color: '#1a1a1a' }}>{orderDetails.order.size}</span>
                    </div>
                  )}

                  {orderDetails.order.color && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#666' }}>Color:</span>
                      <span style={{ color: '#1a1a1a' }}>{orderDetails.order.color}</span>
                    </div>
                  )}
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(124, 58, 237, 0.2)',
                    marginTop: '5px'
                  }}>
                    <span style={{ color: '#666' }}>Total:</span>
                    <span style={{ fontWeight: '700', color: '#7c3aed', fontSize: '18px' }}>
                      ${orderDetails.amount_total?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <p style={{
              color: '#666',
              fontSize: '14px',
              marginBottom: '25px',
              lineHeight: '1.6'
            }}>
              A confirmation email has been sent to <strong>{orderDetails?.customer_email}</strong>.
              <br />
              Each Mrs. Bum item is handcrafted with care, so please allow 5-7 business days for shipping.
            </p>

            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Link to="/shop/mrsbum">
                <Button 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#7c3aed',
                    borderColor: '#7c3aed'
                  }}
                >
                  <ShoppingBag size={18} />
                  Continue Shopping
                </Button>
              </Link>
              <Link to="/">
                <Button 
                  variant="outline"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <ArrowLeft size={18} />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

export default MrsBumSuccess;
